/**
 * A single task card — shared by the /tasks page and the in-app TaskPanel. Beyond the status control it
 * adds: ↗ Open source (deep-links back to the screen the task was created on, incl. ?tab=), a lazy-loaded
 * annotated screenshot thumbnail, a context chip, and 🗑 Delete (delegator/assignee). The 'all' board view
 * is read-only (GM/agent may be neither party). Status/delete errors surface inline; server enforces RBAC.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  setTaskStatus, deleteTask, getTask, TASK_STATUS_LABEL, TASK_PRIORITY_LABEL, type TaskRow,
} from '../lib/api';

const STATUSES = ['backlog', 'todo', 'in_progress', 'in_review', 'done', 'cancelled'];

export default function TaskCard({ t, box, onChanged }: { t: TaskRow; box: 'inbox' | 'outbox' | 'all'; onChanged: () => void }) {
  const nav = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);
  const readOnly = box === 'all';

  const statusMut = useMutation({
    mutationFn: (s: string) => setTaskStatus(t.id, s),
    onSuccess: onChanged,
    onError: (e: any) => setErr(e?.body?.detail ?? e?.body?.error ?? e?.message ?? '更新失败 / failed'),
  });
  const delMut = useMutation({
    mutationFn: () => deleteTask(t.id),
    onSuccess: onChanged,
    onError: (e: any) => setErr(e?.body?.detail ?? e?.body?.error ?? e?.message ?? '删除失败 / delete failed'),
  });
  // Lazy-fetch the full screenshot only when the user expands the thumbnail (lists omit the blob).
  const shot = useQuery({ queryKey: ['task-shot', t.id], queryFn: () => getTask(t.id), enabled: zoom && !!t.has_screenshot });

  const statusLabel = t.display_status ?? (t.status ? TASK_STATUS_LABEL[t.status] ?? t.status : '—');
  let contextLabel: string | null = null;
  try { if (t.context_json) contextLabel = (JSON.parse(t.context_json) as { label?: string }).label ?? null; } catch { /* ignore */ }

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <strong>{t.title}</strong>
        <span className="pill pill-stage small">{statusLabel}</span>
        {t.priority && <span className="pill small">{TASK_PRIORITY_LABEL[t.priority] ?? t.priority}</span>}
        {contextLabel && <span className="pill pill-muted small">🔗 {contextLabel}</span>}
      </div>
      <div className="row-wrap small muted" style={{ gap: 12, marginBottom: 8 }}>
        {box === 'inbox'
          ? <span>来自 / from: {t.delegator_name ?? '—'}</span>
          : <span>指派给 / to: {t.assignee_name ?? '—'}</span>}
        {box === 'all' && <span>来自 / from: {t.delegator_name ?? '—'}</span>}
        {t.due_date && <span>截止 / due: {t.due_date}</span>}
      </div>
      {t.description && <div className="small" style={{ marginBottom: 8 }}>{t.description}</div>}

      {t.has_screenshot && (
        <div style={{ marginBottom: 8 }}>
          {zoom && shot.data?.screenshot
            ? <img src={shot.data.screenshot} alt="task context" onClick={() => setZoom(false)}
                style={{ maxWidth: '100%', border: '1px solid var(--rule)', borderRadius: 6, cursor: 'zoom-out', display: 'block' }} />
            : <button type="button" className="small" onClick={() => setZoom(true)}>
                {zoom && shot.isLoading ? '加载截图… / loading…' : '🖼 查看截图 / View screenshot'}
              </button>}
        </div>
      )}

      <div className="row-wrap" style={{ gap: 8, alignItems: 'center' }}>
        {t.page_url && (
          <button type="button" className="small" onClick={() => nav(t.page_url!)} title="打开任务来源页面 / open the screen this came from">
            ↗ 打开来源 / Open source
          </button>
        )}
        {!readOnly && (
          <>
            <label className="small muted">状态 / Status</label>
            <select value={t.status ?? 'todo'} disabled={statusMut.isPending} onChange={(e) => statusMut.mutate(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{TASK_STATUS_LABEL[s]}</option>)}
            </select>
            <button type="button" className="small" style={{ color: 'var(--bad)', marginLeft: 'auto' }}
              disabled={delMut.isPending}
              onClick={() => { if (confirm(`删除任务「${t.title}」？/ Delete this task?`)) delMut.mutate(); }}>
              🗑 删除 / Delete
            </button>
          </>
        )}
        {err && <span className="small" style={{ color: 'var(--bad)' }}>{err}</span>}
      </div>
    </div>
  );
}
